import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useRef, useState } from "react";

import { UserContext } from "../contexts";
import {
  Facade,
  Home,
  IconName,
  Series,
} from "../types";
import { getPriceFormatted } from "../util";
import Rendivo from "../util/rendivo";
import Carousel from "./Carousel";
import FloorplanSVG from "./FloorplanSVG";
import Icon from "./Icon";

// @ts-ignore
const SeriesListing = observer(
  (props: {
    series: Series;
    isActive: boolean;
  }) => {
    const [selectedHome, setSelectedHome] = useState<Home | null>(null);
    const [bedrooms, setBedrooms] = useState<string | number>("");
    const [bathrooms, setBathrooms] = useState<string | number>("");
    const [living_areas, setLivingAreas] = useState<string | number>("");
    const [car_spaces, setCarSpaces] = useState<string | number>("");
    const [lot_width, setLotWidth] = useState<string | number>("");
    const [area, setArea] = useState<string | number>("");
    const [width, setWidth] = useState<string | number>("");
    const [length, setLength] = useState<string | number>("");
    const userContext = useContext(UserContext);

    useEffect(() => {
      if(props.series.homes){
        setSelectedHome(props.series.homes[0]);
      }
    }, []);

    useEffect(() => {
      if(selectedHome){
        userContext.setSelectedHome(selectedHome);
      }
    }, [
      selectedHome
    ]);

    useEffect(() => {
      if(props.series.homes){
        const first = props.series.homes[0];
        if(props.series.homes.length > 1){
          const last = props.series.homes.slice(-1)[0];
          setBedrooms(first.bedrooms != last.bedrooms ? first.bedrooms + " - " + last.bedrooms : first.bedrooms);
          setBathrooms(first.bathrooms != last.bathrooms ? first.bathrooms + " - " + last.bathrooms : first.bathrooms);
          setLivingAreas(first.living_areas != last.living_areas ? first.living_areas + " - " + last.living_areas : first.living_areas);
          setCarSpaces(first.car_spaces != last.car_spaces ? first.car_spaces + " - " + last.car_spaces : first.car_spaces);
          setLotWidth(first.lot_width != last.lot_width ? first.lot_width + " - " + last.lot_width : first.lot_width);
          setArea(first.total_area_m != last.total_area_m ? first.total_area_m + " - " + last.total_area_m : first.total_area_m);
          setWidth(first.width != last.width ? first.width + " - " + last.width : first.width);
          setLength(first.length != last.length ? first.length + " - " + last.length : first.length);
        }else{
          setBedrooms(first.bedrooms);
          setBathrooms(first.bathrooms);
          setLivingAreas(first.living_areas);
          setCarSpaces(first.car_spaces);
          setLotWidth(first.lot_width);
          setArea(first.total_area_m);
          setWidth(first.width);
          setLength(first.length);
        }
      }
    }, [
      props.series.homes
    ]);

    const onSlide = (index: number) => {
      //Do Nothing
    };

    const getPopularFacades = (facades: Facade[]) => {
      const popular: Facade[] = [];
      facades.map((facade, index) => {
        if(facade.popular){
          popular.push(facade);
        }
      });
      if(popular.length > 0){
        return popular;
      }else{
        return facades;
      }
    };

    return (
      <div
        className="pyht-series-listing"
        data-id={props.series.id}
      >
        <div className="details">
          <h2 className="series-title">{ props.series.title } Series</h2>
          {props.series.homes && (
            <div className="sizes"><strong>({ props.series.homes.length })</strong> Build Sizes Available</div>
          )}
          <div className="tabs-price">
            {props.series.homes && (
              <div className="tabs">
                {props.series.homes.map((home) => {
                  return (
                    <div key={home.id} 
                      data-id={home.id} 
                      className={`tab ${selectedHome && selectedHome.id == home.id ? "active" : "" }`}
                      onClick={() => setSelectedHome(home)}
                    >
                      { home.title.replace(props.series.title + " ", "").replace(" Narrow", "N").replace(" Dual", "D") }
                    </div>
                  );
                })}
              </div>
            )}
            {selectedHome && (
              <div className="price">
                <span className="from">From </span>
                {getPriceFormatted(selectedHome.price)}
              </div>
            )}
          </div>
          <div className="rooms">
            <div className={"room"}>
              <Icon name={IconName.BED} />
              <span>{bedrooms}</span>
            </div>
            <div className={"room"}>
              <Icon name={IconName.COUCH} />
              <span>{living_areas}</span>
            </div>
            <div className={"room"}>
              <Icon name={IconName.BATH} />
              <span>{bathrooms}</span>
            </div>
            <div className={"room"}>
              <Icon name={IconName.CAR} />
              <span>{car_spaces}</span>
            </div>
            <div className={"room"}>
              <Icon name={IconName.LOT_WIDTH} />
              <span>{lot_width}</span>
            </div>
          </div>
          <div className="dimensions">
            <div className="h6 dimensions-title">House Dimensions</div>
            <div className="dimension">
              <div className="label">Area</div>
              <div className="value">{area}m&sup2;</div>
            </div>
            <div className="dimension">
              <div className="label">Length</div>
              <div className="value">{length}m</div>
            </div>
            <div className="dimension">
              <div className="label">Width</div>
              <div className="value">{width}m</div>
            </div>
          </div>
          <div className="btn">View Variants</div>
        </div>
        <div className="floorplan">
          <div className="floorplan-inner">
            {selectedHome && (
              <FloorplanSVG
                vpbPkid={selectedHome.vpb_pkid}
                svg={selectedHome.floorplan_combined}
                layerIds={[]}
                visibleLayers={[]}
                flipCss={false}
              />
            )}
          </div>
        </div>
        <div className="facades">
          {selectedHome && (
            <Carousel
              onSlide={onSlide}
              hidePagination
              itemWidthPerc={100}
              spacingPx={0}
              className="facades-carousel"
              disableSwipe={true}
              items={getPopularFacades(selectedHome.facades).map((image, index) => {
                return (
                  <div className={"slide"} key={index}>
                    <div className="slide-image">
                      <img src={Rendivo.getFacadeFinishRender(700,image.rendivo_id,null,"",false)} />
                    </div>
                    <div className="slide-title">{ image.title } Facade</div>
                  </div>
                );
              })}
            />
          )}
        </div>
      </div>
    );
  }
);

export default SeriesListing;
